/**
 * realisticTrafficLayer — superpone la capa de tráfico TomTom (Vector Flow
 * Tiles, PBF) sobre la vista realista Google 3D como `Polyline3DInteractiveElement`
 * coloreadas por nivel de congestión.
 *
 * Diferencia con la operativa MapLibre: ésta usa **raster tiles** vía
 * `TrafficLayer.tsx`. Map3D no acepta raster layers arbitrarios, así que
 * descargamos las mismas tiles en formato vector y dibujamos cada segmento
 * como polyline 3D al ras del asfalto.
 *
 * Toggle: `usePinControls.trafficFlowEnabled` (default OFF). Refresh ~75 s
 * con jitter ±10 s — el backend de TomTom solo cambia cada ~60 s, pedir
 * más rápido es desperdicio.
 *
 * Misma `VITE_TOMTOM_API_KEY` que la operativa.
 */
import { usePinControls } from '../../store/realisticPinControls';
import {
  tilesForBbox,
  fetchTrafficTile,
  type BBox,
  type TrafficSegment,
} from '../../utils/tomtomTrafficTiles';

const TT_TILE_ZOOM = 15;
const TT_REFRESH_MS_MIN = 65_000;
const TT_REFRESH_MS_MAX = 85_000;
const TT_ALTITUDE_M = 0.5;
const TT_STROKE_WIDTH = 6;

/** BBox del municipio (mismo orden que `detectCongestion.ts`). */
const SCG_BBOX: BBox = {
  west: 2.190,
  south: 41.438,
  east: 2.228,
  north: 41.468,
};

/** Color por nivel de congestión:
 *   road_closure   → negro
 *   level ≥ 0.75   → verde (fluido)
 *   0.45 ≤ level   → ámbar (denso)
 *   resto          → rojo (atasco) */
function colorForTrafficLevel(level: number, closure: boolean): string {
  if (closure) return '#000000';
  if (level >= 0.75) return '#22c55e';
  if (level >= 0.45) return '#facc15';
  return '#ef4444';
}

/** Clave estable por segmento: coords inicial+final redondeadas a 5 dec.
 *  Permite mantener instancias entre refreshes (sin parpadeo) cuando el
 *  tramo no cambió. */
function segmentKey(seg: TrafficSegment): string {
  const a = seg.coordinates[0];
  const b = seg.coordinates[seg.coordinates.length - 1];
  const r = (n: number) => n.toFixed(5);
  return `${r(a.lat)},${r(a.lng)}|${r(b.lat)},${r(b.lng)}`;
}

interface MountedSegment {
  polyline: HTMLElement;
  appliedColor: string;
}

export function mountRealisticTrafficLayer(mapEl: HTMLElement): () => void {
  const maps3d = (window as any).google?.maps?.maps3d;
  if (!maps3d?.Polyline3DInteractiveElement) {
    return () => undefined;
  }
  const Polyline3DInteractiveElement = maps3d.Polyline3DInteractiveElement;
  const AltitudeMode = maps3d.AltitudeMode;
  const altMode = AltitudeMode?.RELATIVE_TO_GROUND ?? 'RELATIVE_TO_GROUND';

  const apiKey = import.meta.env.VITE_TOMTOM_API_KEY as string | undefined;
  if (!apiKey) {
    console.warn('[realisticTrafficLayer] VITE_TOMTOM_API_KEY no definida — capa no montada');
    return () => undefined;
  }

  /** Polylines actualmente en el DOM, indexadas por `segmentKey`. */
  const mounted = new Map<string, MountedSegment>();
  let timer: number | null = null;
  let inFlight = false;
  let disposed = false;

  function unmountAll() {
    for (const { polyline } of mounted.values()) polyline.remove();
    mounted.clear();
  }

  function buildPolyline(seg: TrafficSegment, color: string): HTMLElement {
    return new Polyline3DInteractiveElement({
      coordinates: seg.coordinates.map(c => ({
        lat: c.lat,
        lng: c.lng,
        altitude: TT_ALTITUDE_M,
      })),
      altitudeMode: altMode,
      strokeColor: color,
      strokeWidth: TT_STROKE_WIDTH,
      drawsOccludedSegments: true,
    }) as HTMLElement;
  }

  /** Reconcilia mounted contra el snapshot recién descargado. */
  function reconcile(snapshot: TrafficSegment[]) {
    const seen = new Set<string>();
    for (const seg of snapshot) {
      const key = segmentKey(seg);
      // Dedupe dentro del mismo ciclo (tiles vecinos comparten bordes).
      if (seen.has(key)) continue;
      seen.add(key);
      const color = colorForTrafficLevel(seg.trafficLevel, seg.roadClosure);
      const existing = mounted.get(key);
      if (existing) {
        if (existing.appliedColor !== color) {
          (existing.polyline as any).strokeColor = color;
          existing.appliedColor = color;
        }
        continue;
      }
      const polyline = buildPolyline(seg, color);
      mapEl.appendChild(polyline as Node);
      mounted.set(key, { polyline, appliedColor: color });
    }
    // Unmount los que ya no están.
    for (const [key, m] of mounted) {
      if (!seen.has(key)) {
        m.polyline.remove();
        mounted.delete(key);
      }
    }
  }

  async function refresh() {
    if (disposed || inFlight) return;
    inFlight = true;
    try {
      const tiles = tilesForBbox(SCG_BBOX, TT_TILE_ZOOM);
      const results = await Promise.all(
        tiles.map(t => fetchTrafficTile(TT_TILE_ZOOM, t.x, t.y, apiKey).catch(err => {
          console.warn('[realisticTrafficLayer] tile fetch failed', t, err);
          return [] as TrafficSegment[];
        })),
      );
      const flat = results.flat();
      if (disposed) return;
      reconcile(flat);
    } finally {
      inFlight = false;
    }
  }

  function scheduleNext() {
    if (disposed) return;
    const jitter = Math.floor(Math.random() * (TT_REFRESH_MS_MAX - TT_REFRESH_MS_MIN + 1));
    const wait = TT_REFRESH_MS_MIN + jitter;
    timer = window.setTimeout(async () => {
      timer = null;
      if (!usePinControls.getState().trafficFlowEnabled) return;
      await refresh();
      scheduleNext();
    }, wait);
  }

  async function start() {
    if (timer != null) return;
    await refresh();
    scheduleNext();
  }

  function stop() {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
    unmountAll();
  }

  // Estado inicial — el store puede arrancar con el toggle ya en ON
  // (ej. recarga manual con localStorage si en el futuro se persistiera).
  if (usePinControls.getState().trafficFlowEnabled) {
    void start();
  }

  const unsub = usePinControls.subscribe((s, prev) => {
    if (s.trafficFlowEnabled === prev.trafficFlowEnabled) return;
    if (s.trafficFlowEnabled) {
      void start();
    } else {
      stop();
    }
  });

  return () => {
    disposed = true;
    unsub();
    stop();
  };
}
